import React, { useState, useEffect } from 'react';
import { Commission, Agent } from '@/api/entities';
import { useUniversity } from '@/hooks/useUniversity';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DollarSign, Percent, Plus, Calculator, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CommissionSlabManager from '../components/commissions/CommissionSlabManager';
import SlabBasedCalculator from '../components/commissions/SlabBasedCalculator';
import AddCommissionModal from '../components/commissions/AddCommissionModal';

const statusConfig = {
    pending: { color: 'bg-yellow-200 text-yellow-800' },
    approved: { color: 'bg-blue-200 text-blue-800' },
    paid: { color: 'bg-green-200 text-green-800' },
    disputed: { color: 'bg-red-200 text-red-800' },
};

const DEFAULT_SLABS = [
  { id: 1, minEnrollments: 0, maxEnrollments: 5, rate: 3, description: 'Starter' },
  { id: 2, minEnrollments: 6, maxEnrollments: 15, rate: 10, description: 'Bronze' },
  { id: 3, minEnrollments: 16, maxEnrollments: 30, rate: 15, description: 'Silver' },
  { id: 4, minEnrollments: 31, maxEnrollments: 50, rate: 20, description: 'Gold' },
  { id: 5, minEnrollments: 51, maxEnrollments: 100, rate: 25, description: 'Platinum' },
  { id: 6, minEnrollments: 101, maxEnrollments: null, rate: 50, description: 'Diamond' }
];

export default function UniversityCommissions() {
    const { activeUniversity, isLoading: universityLoading } = useUniversity();
    const [commissions, setCommissions] = useState([]);
    const [agents, setAgents] = useState([]);
    const [filteredCommissions, setFilteredCommissions] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('all');
    const [commissionSlabs, setCommissionSlabs] = useState(DEFAULT_SLABS);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    useEffect(() => {
        if (activeUniversity?.settings?.commission_slabs) {
            setCommissionSlabs(activeUniversity.settings.commission_slabs);
        }
    }, [activeUniversity]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [commsData, agentsData] = await Promise.all([
                    Commission.list('created_at', false),
                    Agent.list()
                ]);
                setCommissions(commsData);
                setFilteredCommissions(commsData);
                setAgents(agentsData);
            } catch (error) {
                console.error('Error loading commission data:', error);
            }
            setIsLoading(false);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedAgent === 'all') {
            setFilteredCommissions(commissions);
        } else {
            setFilteredCommissions(commissions.filter(c => c.agent_id === selectedAgent));
        }
    }, [selectedAgent, commissions]);

    const handleCommissionUpdate = async (id, field, value) => {
        const commission = commissions.find(c => c.id === id);
        if (commission) {
            let numValue = parseFloat(value);
            if(isNaN(numValue)) numValue = 0;

            let finalCommission = commission.base_amount * (commission.commission_rate / 100);

            if(field === 'adjustment_amount'){
                finalCommission += numValue
            } else {
                finalCommission += commission.adjustment_amount;
            }

            await Commission.update(id, { [field]: value, commission_amount: finalCommission });
            
            // Refresh local state
            const updatedCommissions = commissions.map(c => 
                c.id === id ? { ...c, [field]: value, commission_amount: finalCommission } : c
            );
            setCommissions(updatedCommissions);
        }
    };
    
    const handleCommissionAdded = () => {
        const fetchData = async () => {
            const [commsData, agentsData] = await Promise.all([
                Commission.list('created_at', false),
                Agent.list()
            ]);
            setCommissions(commsData);
            setAgents(agentsData);
        };
        fetchData();
        setIsAddModalOpen(false);
    };
    
    const commissionTotals = filteredCommissions.reduce((acc, curr) => {
        const total = (curr.commission_amount || 0) + (curr.adjustment_amount || 0);
        acc.total += total;
        if(curr.status === 'paid') acc.paid += total;
        if(curr.status === 'pending' || curr.status === 'approved') acc.outstanding += total;
        return acc;
    }, { total: 0, paid: 0, outstanding: 0 });

    if (isLoading || universityLoading) return <Skeleton className="h-screen w-full" />;

    return (
        <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-neumorphic mb-2">Advanced Commission Management</h1>
                        <p className="text-gray-600">Slab-based calculation, tracking, and management of agent commissions.</p>
                    </div>
                    <Button 
                        onClick={() => setIsAddModalOpen(true)}
                        className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
                    >
                        <Plus className="w-5 h-5" />
                        Add Commission
                    </Button>
                </div>
                
                <Tabs defaultValue="calculator" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 neumorphic-inset p-1 rounded-2xl mb-6">
                        <TabsTrigger value="calculator" className="neumorphic-tab-trigger flex items-center gap-2">
                            <Calculator className="w-4 h-4"/>
                            Slab Calculator
                        </TabsTrigger>
                        <TabsTrigger value="management" className="neumorphic-tab-trigger flex items-center gap-2">
                            <DollarSign className="w-4 h-4"/>
                            Commission Tracking
                        </TabsTrigger>
                        <TabsTrigger value="settings" className="neumorphic-tab-trigger flex items-center gap-2">
                            <Settings className="w-4 h-4"/>
                            Slab Settings
                        </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="calculator">
                        <SlabBasedCalculator 
                            slabs={commissionSlabs}
                            onCalculate={(result) => console.log('Commission calculated:', result)}
                        />
                    </TabsContent>
                    
                    <TabsContent value="management">
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <Card className="neumorphic rounded-3xl p-6"><CardTitle>Total Commissions</CardTitle><p className="text-2xl font-bold">${commissionTotals.total.toLocaleString()}</p></Card>
                                <Card className="neumorphic rounded-3xl p-6"><CardTitle>Total Paid</CardTitle><p className="text-2xl font-bold">${commissionTotals.paid.toLocaleString()}</p></Card>
                                <Card className="neumorphic rounded-3xl p-6"><CardTitle>Outstanding</CardTitle><p className="text-2xl font-bold">${commissionTotals.outstanding.toLocaleString()}</p></Card>
                            </div>

                            <Card className="neumorphic rounded-3xl">
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="text-lg font-semibold text-neumorphic">Commission Tracking</CardTitle>
                                    <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                                        <SelectTrigger className="w-[250px] neumorphic-inset rounded-xl">
                                            <SelectValue placeholder="Filter by Agent" />
                                        </SelectTrigger>
                                        <SelectContent className="neumorphic">
                                            <SelectItem value="all">All Agents</SelectItem>
                                            {agents.map(agent => <SelectItem key={agent.id} value={agent.id}>{agent.company_name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Student</TableHead>
                                                <TableHead>Base Amount</TableHead>
                                                <TableHead>Rate</TableHead>
                                                <TableHead>Adjustment</TableHead>
                                                <TableHead>Final Commission</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredCommissions.map(c => (
                                                <TableRow key={c.id}>
                                                    <TableCell>{c.student_name}<br/><span className="text-xs text-gray-500">{c.program}</span></TableCell>
                                                    <TableCell>${(c.base_amount || 0).toLocaleString()}</TableCell>
                                                    <TableCell>{c.commission_rate}%</TableCell>
                                                    <TableCell>
                                                        <Input 
                                                            type="number"
                                                            defaultValue={c.adjustment_amount} 
                                                            onBlur={(e) => handleCommissionUpdate(c.id, 'adjustment_amount', e.target.value)}
                                                            className="w-24 neumorphic-inset rounded-lg"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-bold">${((c.commission_amount || 0) + (c.adjustment_amount || 0)).toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        <Badge className={`${statusConfig[c.status]?.color} capitalize`}>{c.status}</Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Total Row */}
                                            <TableRow className="border-t-2 border-gray-300 bg-gray-50">
                                                <TableCell className="font-bold">TOTAL</TableCell>
                                                <TableCell className="font-bold">${filteredCommissions.reduce((sum, c) => sum + (c.base_amount || 0), 0).toLocaleString()}</TableCell>
                                                <TableCell>-</TableCell>
                                                <TableCell className="font-bold">${filteredCommissions.reduce((sum, c) => sum + (c.adjustment_amount || 0), 0).toLocaleString()}</TableCell>
                                                <TableCell className="font-bold text-lg">${commissionTotals.total.toLocaleString()}</TableCell>
                                                <TableCell>-</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                    
                    <TabsContent value="settings">
                        <CommissionSlabManager 
                            onSlabsChange={setCommissionSlabs}
                            initialSlabs={commissionSlabs}
                        />
                    </TabsContent>
                </Tabs>
            </div>
            <style>{`
                .neumorphic-tab-trigger[data-state="active"] {
                    background: var(--neumorphic-bg);
                    box-shadow: 4px 4px 8px var(--neumorphic-dark), -4px -4px 8px var(--neumorphic-light);
                    color: var(--neumorphic-accent);
                }
                .neumorphic-tab-trigger {
                    border-radius: 1rem;
                }
            `}</style>
            
            <AddCommissionModal 
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onCommissionAdded={handleCommissionAdded}
            />
        </div>
    );
}